import {
  Alert,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { EditReviewerContainer } from "./styles";
import { AlertLineReviewerRef } from "../../models/alert-line-reviewer/alert-line-reviewer-ref";
import { AlertLineURLRef } from "../../models/alert-line-url/alert-line-url-ref";
import { CustomDate } from "../../models/custom-date/custom-date";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { UsersApiService } from "../../services/users/users-api.service";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";

interface Props {
  effect: "edit" | "add";
  alertLineURL: AlertLineURLRef;
  item?: AlertLineReviewerRef;
  role?: keyof Pick<AlertLineReviewerRef, "asHR" | "asManager" | "asCompanyCommitteeMember">;
  handleUpdateParent: (reviewer: AlertLineReviewerRef) => void;
}

interface State {
  open: boolean;
  item: AlertLineReviewerRef;
  CCDOpen: boolean;
  error: string;
}

const AddEditAlertlineURLReviewer = ({ effect, alertLineURL, item, role, handleUpdateParent }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_alert_line_url.add_edit_alert_line_url_reviewer";
  const theme = useTheme();

  const DEFAULT_ITEM: Partial<AlertLineReviewerRef> = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      alertLineURL: { id: alertLineURL.id } as AlertLineURLRef,
      user: null,
      asHR: role === "asHR",
      asManager: role === "asManager",
      asCompanyCommitteeMember: role === "asCompanyCommitteeMember",
      fromDate: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      },
      toDate: null,
      viewOnly: false,
    }),
    [alertLineURL, role]
  );

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      item: item ?? (DEFAULT_ITEM as AlertLineReviewerRef),
      CCDOpen: false,
      error: "",
    }),
    [item, DEFAULT_ITEM]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const getRoleReviewers = useCallback(
    (_role: "asHR" | "asManager" | "asCompanyCommitteeMember") => {
      const reviewerUserIds = alertLineURL.reviewers
        .filter((reviewer) => reviewer[_role as keyof AlertLineReviewerRef] === true && reviewer.id !== item?.id)
        .flatMap((reviewer) => reviewer.user);
      return reviewerUserIds;
    },
    [alertLineURL]
  );

  const userAlreadyAssignedtoThisRole = useCallback(() => {
    return getRoleReviewers(state.item.asHR ? "asHR" : state.item.asManager ? "asManager" : "asCompanyCommitteeMember")
      .flatMap((reviewer) => reviewer?.id)
      .includes(state.item.user?.id);
  }, [state.item]);

  useEffect(() => {
    if (userAlreadyAssignedtoThisRole()) {
      setState((state) => ({
        ...state,
        error: "This user is already assigned to this role",
      }));
    } else {
      setState((state) => ({
        ...state,
        error: "",
      }));
    }
  }, [state.item.user, state.item.asHR, state.item.asManager, state.item.asCompanyCommitteeMember]);

  const handleChange = useCallback(
    (field: keyof AlertLineReviewerRef, value: AlertLineReviewerRef[keyof AlertLineReviewerRef]) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [field]: value,
        },
      }));
    },
    [state.item.asHR, state.item.asManager, state.item.user?.id]
  );

  const handleChangeReviewerRole = useCallback(
    (value: "asHR" | "asManager" | "asCompanyCommitteeMember") => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          asHR: value === "asHR",
          asManager: value === "asManager",
          asCompanyCommitteeMember: value === "asCompanyCommitteeMember",
        },
      }));
    },
    [state.item.user?.id]
  );

  const handleConfirm = useCallback(() => {
    setState(INITIAL_STATE);
    handleUpdateParent && handleUpdateParent({ ...state.item, ...(effect === "add" && { id: uuid() }) });
  }, [handleUpdateParent, state.item, effect, INITIAL_STATE]);

  return (
    <>
      <AddEditButton
        effect={effect}
        title={`${t(effect === "add" ? `${tRP}.add_title` : `${tRP}.edit_title`)}`}
        onClick={() => setState((state) => ({ ...state, open: true }))}
        customStyle={{
          width: "30px",
          height: "30px",
          borderRadius: "5px",
        }}
        {...(effect === "edit" && {
          customStyle: {
            width: "max-content",
            fontSize: "5px",
          },
          children: <Typography style={{ padding: "5px", color: theme.palette.text.primary }}>Edit</Typography>,
        })}
      />
      <Dialog open={state.open}>
        <DialogTitle
          title={`${t(effect === "add" ? `${tRP}.add_title` : `${tRP}.edit_title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent style={{ gap: "10px" }}>
          {state.error && <Alert severity="error">{state.error}</Alert>}
          <EditReviewerContainer>
            <AutocompleteDropdown
              serviceApi={new UsersApiService()}
              field={"user"}
              handleChange={handleChange}
              labelKey={`${tRP}.fields.user`}
              selectedValue={state.item.user}
              propsFilter={{
                notIds: [
                  ...getRoleReviewers(
                    state.item?.asHR ? "asHR" : state.item?.asManager ? "asManager" : "asCompanyCommitteeMember"
                  ).flatMap((rev) => rev?.id ?? ""),
                ],
              }}
            />
            <DesktopDatePicker
              value={CustomDate.ToDate(state.item.fromDate)}
              onChange={(newValue) => handleChange("fromDate", newValue ? CustomDate.FromDate(newValue) : newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t(`${tRP}.fields.from_date`)}`}
                />
              )}
            />
            <DesktopDatePicker
              value={CustomDate.ToDate(state.item.toDate)}
              onChange={(newValue) => handleChange("toDate", newValue ? CustomDate.FromDate(newValue) : newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t(`${tRP}.fields.to_date`)}`}
                />
              )}
            />
            <FormControlLabel
              label={`${t(`${tRP}.fields.view_only`)}`}
              control={
                <Checkbox
                  checked={state.item.viewOnly}
                  onChange={(e) => handleChange("viewOnly", e.currentTarget.checked)}
                />
              }
            />
            <Typography
              variant="body2"
              style={{ color: theme.palette.secondary.light }}
            >{`${t(`${tRP}.fields.view_only_note`)}`}</Typography>
            <Typography>{`${t(`${tRP}.fields.authorized_to_review_as`)}`} :</Typography>
            <RadioGroup
              value={
                state.item.asHR
                  ? "asHR"
                  : state.item.asManager
                  ? "asManager"
                  : state.item.asCompanyCommitteeMember
                  ? "asCompanyCommitteeMember"
                  : ""
              }
              onChange={(e) =>
                handleChangeReviewerRole(
                  e.currentTarget.value as keyof Pick<
                    AlertLineReviewerRef,
                    "asHR" | "asManager" | "asCompanyCommitteeMember"
                  >
                )
              }
            >
              <FormControlLabel
                label={`${t(`${tRP}.fields.hr`)}`}
                value={"asHR"}
                control={<Radio size="small" />}
              />
              <FormControlLabel
                label={`${t(`${tRP}.fields.manager`)}`}
                value={"asManager"}
                control={<Radio size="small" />}
              />
              <FormControlLabel
                label={`${t(`${tRP}.fields.company_commitee_member`)}`}
                value={"asCompanyCommitteeMember"}
                control={<Radio size="small" />}
              />
            </RadioGroup>
          </EditReviewerContainer>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleConfirm}
          customSaveName="Confirm"
          disabledSaveButton={!state.item.user || !!state.error}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState(INITIAL_STATE)}
      />
    </>
  );
};
export default AddEditAlertlineURLReviewer;
